// src/components/agora/AgoraWrapper.tsx
import { ReactNode, useEffect, useState, createContext } from "react";
import { AgoraRTCProvider, useRTCClient } from "agora-rtc-react";
import AgoraRTC from "agora-rtc-react";

export const AgoraRoleReadyContext = createContext(false);

export default function AgoraWrapper({
  mode = "live",
  role = "audience",
  codec = "vp8",
  children,
}: {
  mode?: "live" | "rtc";
  role?: "host" | "audience";
  codec?: "vp8" | "h264";
  children: ReactNode;
}) {
  AgoraRTC.enableLogUpload();
  AgoraRTC.setLogLevel(0);

  const [roleReady, setRoleReady] = useState(mode !== "live");

  const client = useRTCClient(
    AgoraRTC.createClient({ mode, codec }),
  );

  useEffect(() => {
    if (mode !== "live") {
      setRoleReady(true);
      return;
    }

    // FIX: audience MUST use { level: 2 } (ultra-low latency).
    //
    // Without level: 2, Agora puts the audience client in standard latency
    // mode. In standard mode the "user-published" event does not fire
    // reliably, so useRemoteVideoTracks() never receives the host's tracks
    // and the video area stays blank — even though the host is live.
    //
    // level: 1 = standard latency  (~3–5 s delay, user-published unreliable)
    // level: 2 = ultra-low latency (~400 ms delay, user-published fires correctly)
    //
    // Host role does not take a clientRoleOptions argument — omit it.
    const roleOptions = role === "audience" ? { level: 2 } : undefined;

    client
      .setClientRole(role, roleOptions as any)
      .then(() => setRoleReady(true))
      .catch((err) => {
        console.error("Failed to set Agora client role:", err);
        setRoleReady(true); // allow join anyway to avoid permanent block
      });
  }, [client, mode, role]);

  const shouldRenderChildren = mode !== "live" || roleReady;

  return (
    <AgoraRoleReadyContext.Provider value={roleReady}>
      <AgoraRTCProvider client={client}>
        {shouldRenderChildren ? children : null}
      </AgoraRTCProvider>
    </AgoraRoleReadyContext.Provider>
  );
}